import {
  ClassEnrollmentsApi,
  type GetEnrollmentResponse,
} from "@/openapi/generated";
import { createAuthenticatedApi } from "@/lib/utils";

export const STUDIO_ENROLLMENTS_PAGE_SIZE = 1000;
const MAX_PAGES = 500;

export interface FetchAllStudioEnrollmentsOptions {
  accessToken: string;
  pageSize?: number;
  /**
   * @deprecated Scroll endpoint returns enrollments for active students only.
   */
  active?: boolean;
}

function parseLastSelectedId(raw: unknown): number | undefined {
  if (raw == null) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * Loads studio enrollments for dashboard/widgets via
 * GET /api/class-enrollments/scroll (active students only).
 */
export async function fetchAllStudioEnrollments({
  accessToken,
  pageSize = STUDIO_ENROLLMENTS_PAGE_SIZE,
}: FetchAllStudioEnrollmentsOptions): Promise<GetEnrollmentResponse[]> {
  const service = createAuthenticatedApi(ClassEnrollmentsApi, accessToken);
  const allItems: GetEnrollmentResponse[] = [];
  const seenEnrollmentIds = new Set<number>();
  let lastSelectedId: number | undefined;
  let iterations = 0;

  while (iterations < MAX_PAGES) {
    const pageResult = await service.apiClassEnrollmentsScrollGet({
      page: 1,
      pageSize,
      lastSelectedId,
    });

    const items = pageResult.items ?? [];
    if (items.length === 0) break;

    for (const item of items) {
      const enrollmentId = Number(item.enrollmentId);
      if (Number.isFinite(enrollmentId) && enrollmentId > 0) {
        if (seenEnrollmentIds.has(enrollmentId)) continue;
        seenEnrollmentIds.add(enrollmentId);
      }
      allItems.push(item);
    }

    if (pageResult.hasNextPage !== true) break;

    const cursor =
      parseLastSelectedId(pageResult.lastSelectedId) ??
      parseLastSelectedId(items[items.length - 1]?.enrollmentId);
    if (cursor == null) break;

    lastSelectedId = cursor;
    iterations += 1;
  }

  return allItems;
}
